"use client";

import { useCallback, useMemo, useState } from "react";
import { useTalentSearchResults } from "@/components/search/talent-search-results-provider";
import type { SearchCriterion } from "@/hooks/use-search-definition-draft";

export const MAX_CRITERIA = 20;
const EMPTY_CRITERIA: readonly SearchCriterion[] = [];

export interface SearchCriterionDraft {
  id: string;
  label: string;
  description: string;
  position: number;
}

let localIdSeq = 0;
/** Stable per-mount id for a criterion row (the draft itself carries none). */
function localId() {
  localIdSeq += 1;
  return `local-${localIdSeq}`;
}

function contentKey(items: readonly { label: string; description: string }[]) {
  return JSON.stringify(items.map((c) => [c.label, c.description] as const));
}

function withPositions<
  T extends { id: string; label: string; description: string },
>(items: T[]): SearchCriterionDraft[] {
  return items.map((c, i) => ({
    id: c.id,
    label: c.label,
    description: c.description,
    position: i + 1,
  }));
}

function seedDrafts(criteria: readonly SearchCriterion[]) {
  return withPositions(
    criteria.map((c) => ({
      id: localId(),
      label: c.label,
      description: c.description,
    })),
  );
}

export interface SearchCriteriaDraftsState {
  drafts: SearchCriterionDraft[];
  adding: boolean;
  setAdding: (next: boolean) => void;
  atCap: boolean;
  /** Local rows differ from the search draft's criteria. */
  dirty: boolean;
  add: (item: { label: string; description: string }) => void;
  update: (id: string, item: { label: string; description: string }) => void;
  remove: (id: string) => void;
  move: (activeId: string, overId: string) => void;
  /** Write the local rows into the search draft (Save is Run: nothing runs). */
  save: () => void;
}

/**
 * Local row state for editing a search's ranking criteria. Seeded from the
 * results provider's unsaved definition draft and re-seeded whenever the
 * search or the draft's criteria change underneath it (agent apply, reset);
 * `save()` writes the rows back into that draft, where the Run button picks
 * them up. Shared by the toolbar sheet and the filter-panel pane so the list
 * body and the Apply action can live in different containers.
 */
export function useSearchCriteriaDrafts({
  onSaved,
}: {
  onSaved?: () => void;
} = {}): SearchCriteriaDraftsState {
  const { searchId, draft, setCriteria } = useTalentSearchResults();
  const criteria = draft.criteria ?? EMPTY_CRITERIA;

  const [drafts, setDrafts] = useState<SearchCriterionDraft[]>(() =>
    seedDrafts(criteria),
  );
  const [adding, setAdding] = useState(false);

  const serverKey = useMemo(() => contentKey(criteria), [criteria]);
  const draftKey = useMemo(() => contentKey(drafts), [drafts]);
  const dirty = serverKey !== draftKey;

  // Re-seed when the draft's criteria change underneath us (search switch,
  // agent apply, reset, or our own save round-tripping). Keyed on content,
  // not array identity: `draft.criteria` is a new array whenever any other
  // part of the draft changes. Adjusted during render rather than in an
  // effect so the stale rows never reach the screen.
  const seedKey = `${searchId}:${serverKey}`;
  const [seededFor, setSeededFor] = useState(seedKey);
  if (seededFor !== seedKey) {
    setSeededFor(seedKey);
    // Rows that already match keep their ids so the cards do not remount.
    if (draftKey !== serverKey) setDrafts(seedDrafts(criteria));
  }

  const atCap = drafts.length >= MAX_CRITERIA;

  const add = useCallback(
    (item: { label: string; description: string }) => {
      setDrafts((prev) => {
        if (prev.length >= MAX_CRITERIA) return prev;
        return withPositions([...prev, { id: localId(), ...item }]);
      });
      setAdding(false);
    },
    [],
  );

  const update = useCallback(
    (id: string, item: { label: string; description: string }) => {
      setDrafts((prev) =>
        prev.map((d) => (d.id === id ? { ...d, ...item } : d)),
      );
    },
    [],
  );

  const remove = useCallback((id: string) => {
    setDrafts((prev) => withPositions(prev.filter((d) => d.id !== id)));
  }, []);

  const move = useCallback((activeId: string, overId: string) => {
    if (activeId === overId) return;
    setDrafts((prev) => {
      const oldIdx = prev.findIndex((d) => d.id === activeId);
      const newIdx = prev.findIndex((d) => d.id === overId);
      if (oldIdx === -1 || newIdx === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(oldIdx, 1);
      next.splice(newIdx, 0, moved);
      return withPositions(next);
    });
  }, []);

  const save = useCallback(() => {
    setCriteria(
      drafts.map(({ label, description }) => ({ label, description })),
    );
    onSaved?.();
  }, [drafts, onSaved, setCriteria]);

  return {
    drafts,
    adding,
    setAdding,
    atCap,
    dirty,
    add,
    update,
    remove,
    move,
    save,
  };
}
